import { z } from 'zod';

const text = z.string().trim().min(1).max(2000);
export const agentToneSchema = z.object({
  formality: z.enum(['CASUAL', 'NEUTRAL', 'FORMAL']),
  warmth: z.enum(['RESERVED', 'BALANCED', 'WARM']),
  brevity: z.enum(['CONCISE', 'BALANCED', 'DETAILED']),
  humour: z.enum(['NONE', 'LIGHT']),
  empathy: z.enum(['NEUTRAL', 'SUPPORTIVE']),
  approvedPhrases: z.array(text).max(30),
  prohibitedPhrases: z.array(text).max(30),
  exampleAnswers: z.array(text).max(10),
}).strict();

export const agentPolicySchema = z.object({
  prohibitedPhrases: z.array(text).max(30),
  restrictedTopics: z.array(text).max(30),
  escalationTopics: z.array(text).max(30),
}).strict();

export const messageEligibilitySchema = z.object({
  markets: z.array(z.string().regex(/^[A-Z]{2}$/)).max(50),
  channels: z.array(z.enum(['web', 'whatsapp', 'ivr'])).min(1).max(3),
  languages: z.array(z.literal('en')).min(1).max(1),
  requiresAuthenticatedCustomer: z.boolean(),
  requiresBooking: z.boolean(),
}).strict();

export const scheduledAgentMessageSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['MESSAGE', 'OFFER_PRESENTATION']),
  wording: text,
  sourceReference: text,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  timezone: z.string().max(100).refine((value) => {
    try { new Intl.DateTimeFormat('en', { timeZone: value }); return true; } catch { return false; }
  }, 'Use a valid IANA timezone'),
  priority: z.number().int().min(0).max(1000),
  eligibility: messageEligibilitySchema,
  stacking: z.literal('NONE'),
  suppressInSensitiveContext: z.literal(true),
  oncePerSession: z.literal(true),
}).strict().refine((value) => Date.parse(value.startsAt) < Date.parse(value.endsAt), {
  message: 'endsAt must be after startsAt', path: ['endsAt'],
});

export const agentConfigurationContentSchema = z.object({
  tone: agentToneSchema,
  policy: agentPolicySchema,
  messages: z.array(scheduledAgentMessageSchema).max(50),
}).strict().superRefine((value, context) => {
  const ids = value.messages.map((message) => message.id);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: 'custom', message: 'Message IDs must be unique', path: ['messages'] });
  const forbidden = [...value.tone.prohibitedPhrases, ...value.policy.prohibitedPhrases].map((phrase) => phrase.toLowerCase());
  for (const phrase of [...value.tone.approvedPhrases, ...value.tone.exampleAnswers, ...value.messages.map((message) => message.wording)]) {
    if (forbidden.some((term) => phrase.toLowerCase().includes(term))) context.addIssue({ code: 'custom', message: 'Approved wording conflicts with a prohibited phrase' });
  }
  for (let i = 0; i < value.messages.length; i++) for (const right of value.messages.slice(i + 1)) {
    const left = value.messages[i]!;
    const intersects = (a: string[], b: string[]) => !a.length || !b.length || a.some((item) => b.includes(item));
    if (left.priority === right.priority && Date.parse(left.startsAt) < Date.parse(right.endsAt) && Date.parse(right.startsAt) < Date.parse(left.endsAt) &&
        intersects(left.eligibility.markets, right.eligibility.markets) && intersects(left.eligibility.channels, right.eligibility.channels)) {
      context.addIssue({ code: 'custom', message: 'Overlapping messages require distinct priorities', path: ['messages'] });
    }
  }
});

export const createAgentConfigurationDraftSchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  scopeId: z.string().uuid(),
  changeReason: z.string().trim().min(10).max(500),
  content: agentConfigurationContentSchema,
}).strict();
export const agentConfigurationActionSchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  reason: z.string().trim().min(10).max(500),
}).strict();

export const lockedAgentPolicySchema = z.object({
  baselineVersion: z.literal(1),
  identityBeforeProtectedDisclosure: z.literal(true),
  consentBeforeTransaction: z.literal(true),
  authoritativePricesOnly: z.literal(true),
  suppressSensitivePromotions: z.literal(true),
  airlineTransactionExecution: z.literal(false),
  enforcementStatus: z.literal('RUNTIME_NOT_VERIFIED'),
  inheritedGuardrails: z.array(z.object({
    guardrailId: z.string().uuid(), version: z.number().int().positive(),
    scopeId: z.string().uuid(), name: z.string(), instruction: z.string(),
    prohibitedBehavior: z.string(), requiredBehavior: z.string(),
    enforcement: z.string(), deterministicControl: z.string().nullable(),
    objective: z.string(), severity: z.string(), triggerConditions: z.array(z.string()),
    fallbackResponse: z.string(), escalationRoute: z.string().nullable(), policyReference: z.string().nullable(),
    customerExperience: z.string(),
  }).strict()),
}).strict();
export const agentConfigurationPayloadSchema = z.object({
  schemaVersion: z.literal(1), agentId: z.string().uuid(), agentKey: z.string(),
  scopeId: z.string().uuid(),
  scope: z.object({ platform: z.string(), useCase: z.string(), market: z.string(), channel: z.string(), language: z.string(), environment: z.enum(['DEVELOPMENT', 'STAGING', 'PRODUCTION']) }).strict(),
  lockedPolicy: lockedAgentPolicySchema,
  content: agentConfigurationContentSchema,
}).strict();
export const agentConfigurationVersionSchema = z.object({
  version: z.number().int().positive(),
  status: z.enum(['DRAFT', 'AWAITING_APPROVAL', 'APPROVED', 'REJECTED', 'PUBLISHED']),
  contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  payload: agentConfigurationPayloadSchema,
  createdBy: z.string(), approvedBy: z.string().nullable(),
  changeReason: z.string(), createdAt: z.string().datetime(), publishedAt: z.string().datetime().nullable(),
});
export const runtimeActivationSchema = z.object({
  agentId: z.string().uuid(), scopeId: z.string().uuid(), environment: z.enum(['DEVELOPMENT', 'STAGING', 'PRODUCTION']),
  version: z.number().int().positive(), contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  activatedBy: z.string(), activatedAt: z.string().datetime(), invalidatedAt: z.string().datetime().nullable(),
});
export const runtimeStatusSchema = z.object({
  deliveryStatus: z.enum(['NOT_CONNECTED', 'ACTIVE', 'STALE']), runtimeVerified: z.boolean(),
  activation: runtimeActivationSchema.nullable(), reason: z.string().nullable(),
});
export const runtimeActivateSchema = z.object({
  version: z.number().int().positive(), contentHash: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
export const runtimeTurnSchema = z.object({
  message: z.string().trim().min(1).max(4000), channel: z.enum(['web', 'whatsapp', 'ivr']),
  market: z.string().regex(/^[A-Z]{2}$/), language: z.literal('en'), sessionId: z.string().uuid().optional(),
}).strict();
export const runtimeTurnResultSchema = z.object({
  sessionId: z.string().uuid(), sessionRevision: z.number().int().positive(),
  version: z.number().int().positive(), contentHash: z.string(),
  response: z.string(), decision: z.enum(['ANSWER', 'ESCALATE', 'BLOCK']),
  presentedMessageIds: z.array(z.string()), appliedMessageIds: z.array(z.string()), policyReasons: z.array(z.string()),
  agentType: z.enum(['faq', 'booking', 'servicing']), airlineTransactionExecution: z.literal(false),
});
export type RuntimeStatus = z.infer<typeof runtimeStatusSchema>;
export type RuntimeActivation = z.infer<typeof runtimeActivationSchema>;
export type RuntimeActivateInput = z.infer<typeof runtimeActivateSchema>;
export type RuntimeTurnInput = z.infer<typeof runtimeTurnSchema>;
export type RuntimeTurnResult = z.infer<typeof runtimeTurnResultSchema>;
export const agentConfigurationSummarySchema = z.object({
  agentId: z.string().uuid(), revision: z.number().int().nonnegative(),
  scopeId: z.string().uuid().nullable(),
  latestVersion: z.number().int().positive().nullable(),
  publishedVersion: z.number().int().positive().nullable(),
  deliveryStatus: z.enum(['NOT_CONNECTED', 'ACTIVE', 'STALE']),
  runtimeVerified: z.boolean(),
  activation: runtimeActivationSchema.nullable().default(null),
  versions: z.array(agentConfigurationVersionSchema),
});
export const agentConfigurationDeliverySchema = z.object({
  version: z.number().int().positive(), contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  payload: agentConfigurationPayloadSchema, deliveryStatus: z.literal('NOT_CONNECTED'), runtimeVerified: z.literal(false),
});
export type AgentTone = z.infer<typeof agentToneSchema>;
export type AgentPolicy = z.infer<typeof agentPolicySchema>;
export type ScheduledAgentMessage = z.infer<typeof scheduledAgentMessageSchema>;
export type AgentConfigurationContent = z.infer<typeof agentConfigurationContentSchema>;
export type CreateAgentConfigurationDraftInput = z.infer<typeof createAgentConfigurationDraftSchema>;
export type AgentConfigurationActionInput = z.infer<typeof agentConfigurationActionSchema>;
export type AgentConfigurationPayload = z.infer<typeof agentConfigurationPayloadSchema>;
export type AgentConfigurationVersion = z.infer<typeof agentConfigurationVersionSchema>;
export type AgentConfigurationSummary = z.infer<typeof agentConfigurationSummarySchema>;
export type AgentConfigurationDelivery = z.infer<typeof agentConfigurationDeliverySchema>;
